import { ShotData } from '../types';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';

export interface ReportOptions {
  title?: string;
  playerName?: string;
  sessionId?: string;
  dateRange?: { start: Date; end: Date };
  includeCharts?: boolean;
  includeRecommendations?: boolean;
  includeComparison?: boolean;
  format?: 'pdf' | 'excel' | 'csv';
}

export interface ReportData {
  shots: ShotData[];
  statistics: {
    totalShots: number;
    averageDistance: number;
    averagePower: number;
    averageQuality: number;
    bestShot: ShotData | null;
    worstShot: ShotData | null;
    improvementRate: number;
  };
  recommendations: string[];
  chartImages?: string[];
}

export class ReportGenerator {
  private pdf: jsPDF;
  
  constructor() {
    this.pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
  }

  /**
   * Generate a comprehensive PDF report
   */
  async generatePDFReport(data: ReportData, options: ReportOptions = {}): Promise<Blob> {
    const {
      title = 'Cricket Shot Analysis Report',
      playerName = 'Player',
      sessionId,
      includeCharts = true,
      includeRecommendations = true,
      includeComparison = false
    } = options;

    // Reset PDF
    this.pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Add header
    this.addHeader(title, playerName, sessionId);
    
    // Add summary statistics
    this.addSummarySection(data.statistics);
    
    // Add shot details table
    this.addShotDetailsTable(data.shots);
    
    // Add charts if requested
    if (includeCharts && data.chartImages) {
      await this.addChartsSection(data.chartImages);
    }
    
    // Add recommendations
    if (includeRecommendations && data.recommendations.length > 0) {
      this.addRecommendationsSection(data.recommendations);
    }
    
    // Add comparison if requested
    if (includeComparison && data.shots.length > 1) {
      this.addComparisonSection(data.shots);
    }
    
    // Add footer
    this.addFooter();
    
    // Return as blob
    return this.pdf.output('blob');
  }

  /**
   * Add report header
   */
  private addHeader(title: string, playerName: string, sessionId?: string) {
    // Logo/Title area
    this.pdf.setFillColor(59, 130, 246); // Blue
    this.pdf.rect(0, 0, 210, 40, 'F');
    
    // Title
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(24);
    this.pdf.text(title, 105, 20, { align: 'center' });
    
    // Subtitle
    this.pdf.setFontSize(12);
    this.pdf.text(`Player: ${playerName}`, 105, 30, { align: 'center' });
    
    if (sessionId) {
      this.pdf.setFontSize(10);
      this.pdf.text(`Session: ${sessionId}`, 105, 36, { align: 'center' });
    }
    
    // Reset text color
    this.pdf.setTextColor(0, 0, 0);
    this.pdf.setFontSize(12);
  }

  /**
   * Add summary statistics section
   */
  private addSummarySection(stats: ReportData['statistics']) {
    let yPos = 50;
    
    this.pdf.setFontSize(16);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Performance Summary', 20, yPos);

    yPos += 10;
    this.pdf.setFontSize(11);
    this.pdf.setFont('helvetica', 'normal');
    
    // Create summary cards
    const summaryData = [
      ['Total Shots', stats.totalShots.toString()],
      ['Average Distance', `${stats.averageDistance.toFixed(1)}m`],
      ['Average Power', `${(stats.averagePower * 100).toFixed(0)}%`],
      ['Quality Score', `${stats.averageQuality.toFixed(0)}%`],
      ['Improvement Rate', `${stats.improvementRate > 0 ? '+' : ''}${stats.improvementRate.toFixed(1)}%`]
    ];
    
    autoTable(this.pdf, {
      startY: yPos,
      head: [],
      body: summaryData,
      theme: 'grid',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { halign: 'right', cellWidth: 40 }
      },
      margin: { left: 20, right: 110 }
    });
  }

  /**
   * Add shot details table
   */
  private addShotDetailsTable(shots: ShotData[]) {
    const tableData = shots.slice(0, 10).map((shot, index) => [
      `#${index + 1}`,
      shot.shotType || 'Unknown',
      `${shot.distance?.toFixed(1)}m`,
      `${shot.exitVelocity?.toFixed(0)}m/s`,
      `${(shot.powerTransfer || 0) * 100}%`,
      shot.quality || 'N/A'
    ]);
    
    autoTable(this.pdf, {
      startY: 120,
      head: [['Shot', 'Type', 'Distance', 'Speed', 'Power', 'Quality']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: 20, right: 20 }
    });
  }

  /**
   * Add charts section with captured images
   */
  private async addChartsSection(chartImages: string[]) {
    this.pdf.addPage();
    
    let yPos = 20;
    this.pdf.setFontSize(16);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Performance Charts', 20, yPos);
    
    yPos += 20;
    
    // Add each chart image
    for (const chartImage of chartImages.slice(0, 2)) {
      if (yPos > 200) {
        this.pdf.addPage();
        yPos = 20;
      }
      
      try {
        this.pdf.addImage(chartImage, 'PNG', 20, yPos, 170, 100);
        yPos += 110;
      } catch (error) {
        console.error('Error adding chart image:', error);
      }
    }
  }

  /**
   * Add recommendations section
   */
  private addRecommendationsSection(recommendations: string[]) {
    this.pdf.addPage();
    
    let yPos = 20;
    this.pdf.setFontSize(16);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Coaching Recommendations', 20, yPos);
    
    yPos += 15;
    this.pdf.setFontSize(11);
    this.pdf.setFont('helvetica', 'normal');
    
    recommendations.forEach((rec, _index) => {
      if (yPos > 260) {
        this.pdf.addPage();
        yPos = 20;
      }
      
      // Add bullet point
      this.pdf.circle(22, yPos - 2, 1, 'F');
      
      // Add recommendation text with wrapping
      const lines = this.pdf.splitTextToSize(rec, 160);
      lines.forEach((line: string) => {
        this.pdf.text(line, 28, yPos);
        yPos += 6;
      });
      
      yPos += 4;
    });
  }

  /**
   * Add comparison section
   */
  private addComparisonSection(shots: ShotData[]) {
    const bestShot = shots.reduce((best, shot) => 
      (shot.distance || 0) > (best.distance || 0) ? shot : best
    );
    
    const avgDistance = shots.reduce((sum, shot) => sum + (shot.distance || 0), 0) / shots.length;
    
    let yPos = this.pdf.internal.pageSize.height - 60;
    
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Key Insights', 20, yPos);
    
    yPos += 8;
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    
    this.pdf.text(`• Best Shot Distance: ${bestShot.distance?.toFixed(1)}m`, 25, yPos);
    yPos += 6;
    this.pdf.text(`• Average Distance: ${avgDistance.toFixed(1)}m`, 25, yPos);
    yPos += 6;
    this.pdf.text(`• Consistency Score: ${this.calculateConsistency(shots).toFixed(0)}%`, 25, yPos);
  }

  /**
   * Add footer with timestamp
   */
  private addFooter() {
    const pageCount = this.pdf.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      this.pdf.setPage(i);
      
      // Add page number
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(128, 128, 128);
      this.pdf.text(
        `Page ${i} of ${pageCount}`,
        105,
        this.pdf.internal.pageSize.height - 10,
        { align: 'center' }
      );
      
      // Add generation timestamp
      this.pdf.text(
        `Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
        20,
        this.pdf.internal.pageSize.height - 10
      );
      
      // Add SixSight branding
      this.pdf.text(
        'SixSight Cricket Analytics',
        190,
        this.pdf.internal.pageSize.height - 10,
        { align: 'right' }
      );
    }
  }

  /**
   * Calculate consistency score
   */
  private calculateConsistency(shots: ShotData[]): number {
    if (shots.length < 2) return 100;
    
    const distances = shots.map(s => s.distance || 0);
    const mean = distances.reduce((a, b) => a + b, 0) / distances.length;
    const variance = distances.reduce((sum, d) => sum + Math.pow(d - mean, 2), 0) / distances.length;
    const stdDev = Math.sqrt(variance);
    const coefficient = (stdDev / mean) * 100;
    
    return Math.max(0, 100 - coefficient);
  }

  /**
   * Capture element as image for PDF
   */
  static async captureElementAsImage(element: HTMLElement): Promise<string> {
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false
    });
    
    return canvas.toDataURL('image/png');
  }

  /**
   * Generate CSV export
   */
  static generateCSV(shots: ShotData[]): string {
    const headers = [
      'Shot #',
      'Type',
      'Distance (m)',
      'Exit Velocity (m/s)',
      'Launch Angle (°)',
      'Horizontal Angle (°)',
      'Power Transfer (%)',
      'Sweet Spot Distance (mm)',
      'Quality',
      'Timing',
      'Contact'
    ];
    
    const rows = shots.map((shot, index) => [
      index + 1,
      shot.shotType || '',
      shot.distance?.toFixed(1) || '',
      shot.exitVelocity?.toFixed(1) || '',
      shot.launchAngle?.toFixed(1) || '',
      shot.horizontalAngle?.toFixed(1) || '',
      ((shot.powerTransfer || 0) * 100).toFixed(0),
      shot.sweetSpotDistance?.toFixed(1) || '',
      shot.quality || '',
      shot.timing || '',
      shot.footwork || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    return csvContent;
  }

  /**
   * Generate Excel export (as CSV with proper formatting)
   */
  static generateExcel(data: ReportData): string {
    // For now, return CSV format that Excel can open
    // In production, use a library like xlsx for proper Excel format
    return this.generateCSV(data.shots);
  }
}

// Export singleton instance
export const reportGenerator = new ReportGenerator();